import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ComicCard from '@/components/base/ComicCard';
import { comics, categories } from '@/mocks/comics';
import { SeoSchema, getOrganizationSchema, getWebSiteSchema, getWebPageSchema, getFAQSchema } from '@/components/base/SeoSchema';
import type { Comic } from '@/types/comic';

function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const featuredComics = comics.slice(0, 5);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredComics.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [featuredComics.length]);

  return (
    <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
      {featuredComics.map((comic, index) => (
        <div
          key={comic.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={comic.cover}
            alt={comic.title}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>

      <div className="absolute inset-0 flex items-center">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-2xl">
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: 'var(--font-heading)' }}>
              韩漫阅读 - 免费下拉式韩国漫画大全
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-6 leading-relaxed">
              海量无删减韩国漫画免费在线阅读，支持便捷的下拉式浏览体验。恋爱、奇幻、动作、悬疑等多种分类，满足您的各种阅读喜好。
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/category/all"
                className="inline-flex items-center justify-center px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <div className="w-4 h-4 flex items-center justify-center mr-2">
                  <i className="ri-book-read-line"></i>
                </div>
                立即阅读
              </Link>
              <Link
                to="/category/all"
                className="inline-flex items-center justify-center px-6 py-3 bg-white/20 text-white border border-white/30 font-medium rounded-md hover:bg-white/30 transition-colors whitespace-nowrap"
              >
                漫画大全
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {featuredComics.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
              index === currentSlide ? 'bg-white w-6' : 'bg-white/50'
            }`}
            aria-label={`第${index + 1}张`}
          />
        ))}
      </div>
    </section>
  );
}

function CategoryNav() {
  return (
    <section className="bg-background-50 border-b border-background-200/70">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide flex-wrap">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
              className="px-4 py-2 text-sm text-foreground-700 hover:text-primary-500 hover:bg-primary-50 rounded-full border border-background-200/70 hover:border-primary-300 transition-all whitespace-nowrap"
            >
              {cat.name}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function HotToday({ comics: hotComics }: { comics: Comic[] }) {
  return (
    <section className="py-10 md:py-14 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
              <a href="#hot-today">今日热门</a>
            </h2>
            <p className="text-sm text-foreground-500 mt-1">最受欢迎的韩国漫画推荐</p>
          </div>
          <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
            查看全部 <i className="ri-arrow-right-line align-middle"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
          {hotComics.map((comic, index) => (
            <div key={comic.id} className="relative">
              {index < 3 && (
                <div className="absolute top-2 left-2 z-10 w-6 h-6 flex items-center justify-center bg-accent-500 text-background-50 text-xs font-bold rounded-full">
                  {index + 1}
                </div>
              )}
              <ComicCard comic={comic} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestUpdates({ comics: latestComics }: { comics: Comic[] }) {
  return (
    <section className="py-10 md:py-14 bg-background-100">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
              <a href="#latest-updates">最近更新</a>
            </h2>
            <p className="text-sm text-foreground-500 mt-1">最新更新的韩国漫画章节</p>
          </div>
          <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
            查看全部 <i className="ri-arrow-right-line align-middle"></i>
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
          {latestComics.map((comic) => (
            <ComicCard key={comic.id} comic={comic} />
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const faqs = [
    { q: '韩漫网站上的漫画都是免费的吗？', a: '是的，韩漫阅读网站上的所有韩国漫画作品均完全免费阅读，无需付费订阅或购买。我们致力于为广大漫画爱好者提供无删减、完整版的高质量韩漫资源。' },
    { q: '什么是下拉式漫画阅读方式？', a: '下拉式阅读是一种专为手机和网页优化的漫画浏览方式，您只需向下滑动页面即可连续阅读漫画内容，无需翻页点击，带来流畅丝滑的阅读体验。这也是韩国网络漫画最主流的呈现形式。' },
    { q: '网站上的漫画是否有删减？', a: '本站所有漫画均为无删减完整版，保持原作的完整内容和画风，让您体验到原汁原味的韩国漫画作品。' },
    { q: '韩漫网站支持哪些设备阅读？', a: '韩漫阅读网站采用响应式设计，完美支持电脑、平板和手机等多种设备。推荐使用手机竖屏阅读，效果最佳。' },
    { q: '漫画更新频率是怎样的？', a: '本站热门连载漫画通常保持每周更新一话的节奏，部分作品可能会根据作者进度有所调整。您可以收藏喜欢的作品，随时追踪最新章节。' },
  ];

  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-10 md:py-14 bg-background-50">
      <div className="max-w-3xl mx-auto px-4 md:px-6">
        <div className="text-center mb-8">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
            <a href="#faq">常见问题</a>
          </h2>
          <p className="text-sm text-foreground-500">关于韩漫阅读的常见疑问解答</p>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div key={index} className="border border-background-200/70 rounded-lg overflow-hidden">
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-background-50 transition-colors cursor-pointer"
              >
                <span className="text-sm font-medium text-foreground-900 pr-4">{faq.q}</span>
                <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform ${openIndex === index ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-foreground-500"></i>
                </div>
              </button>
              {openIndex === index && (
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const hotComics = [...comics].sort((a, b) => b.views - a.views).slice(0, 10);
  const latestComics = [...comics].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());

  return (
    <>
      <SeoSchema type="Organization" data={getOrganizationSchema() as Record<string, unknown>} />
      <SeoSchema type="WebSite" data={getWebSiteSchema() as Record<string, unknown>} />
      <SeoSchema
        type="WebPage"
        data={getWebPageSchema(
          '韩漫阅读 - 免费下拉式在线漫画 | 好看的无删减韩国漫画大全',
          '访问此韩漫网站，免费阅读无删减韩国漫画大全，体验便捷的下拉式在线浏览，欣赏完整版好看作品，满足您的各种阅读喜好。',
          'https://hanmanyuedu.com'
        ) as Record<string, unknown>}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <HeroSection />
          <CategoryNav />
          <HotToday comics={hotComics} />
          <LatestUpdates comics={latestComics} />
          <FAQSection />
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}